import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BreadCrumbs from "src/components/BreadCrumbs";
import BackButton from "src/components/Button/BackButton";
import { Container } from "src/components/ListScreen";
import LoadingContainer from "src/components/LoadingContainer";
import { HorizontalTabs } from "src/components/Tabs/HorizontalTabs";
import { generateBreadcrumbs } from "src/helpers/breadcrumbs";
import { setPageNameHeader } from "src/redux/actions/header";
import { setOpenedAssetsPage } from "src/redux/actions/openedPages";
import { getPriorityLevels } from "src/redux/selectors/resources";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService } from "src/services/Firebase/data";
import { Content, ContentFrame, TopBar, colors } from "src/styles";
import { Asset, ID } from "src/types";
import styled from "styled-components";
import GeneralInformation from "./GeneralInformation";
import AssetWorkOrders, { AssociatedWorkOrder } from "./AssetWorkOrders";

interface AssetProfileLocationState {
  asset?: Asset;
}

const DEFAULT_PAGE_SIZE = 10;

// ─── Static demo data (mirrors the screenshot) ───────────────────────────────
const DEMO_ASSET = {
  id: "BLVL0230",
  title: "Hot Water Boiler",
  status: "ACTIVE",
  stationName: "BELLEVILLE STATION",
  stationId: "BLVL",
  stationAddress: "250 STATION STREET",
  heritageBuilding: "-",
  category: "HVAC",
  subCategory: "HEAT GENERATING SYSTEMS",
  description: "HOT WATER BOILERS",
  roomLocation: "MAIN BUILDING - ROOM 200",
  manufacturer: "HYDROTHERM",
  model: "KN-20",
  serialNumber: "KN-H-NET-M11NB",
  yearInstalled: "2011",
  capacity: "2000 MBH",
  materialType: "N/A",
  equipmentTag: "BO-1",
  dimensionalInfo: "N/A",
  conditionNotes:
    "Good condition, the unit is operational. Exterior casing is adequate. No performance concerns noted or reported.",
  replacementValue: "$75,000",
  avgServiceLife: "25",
  estimatedRemainingLife: "14",
  overallConditionRating: "2 - GOOD",
  impactToOperations: "2",
  impactToEnvironment: "5",
  impactToHealthSafety: "8",
  systemRedundancy: "3",
  overallCriticalityRating: "18",
  cycle: "25",
  priority: "4- LIFE CYCLE",
  quantity: "1",
  units: "EACH",
  unitRate: "$75,000",
  dateLastInspection: "-",
  dateNextInspection: "-",
  maintenanceFrequency: "MONTHLY",
  maintenanceRemarks: "N/A",
  imageUrl:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6e/Boiler_-_geograph.org.uk_-_1481175.jpg/320px-Boiler_-_geograph.org.uk_-_1481175.jpg",
};

// ─── Tabs ─────────────────────────────────────────────────────────────────────
type Tab = "general" | "workRequests";

const ASSOCIATED_WORK_ORDER_HEADERS = ["Work Order #", "Description", "Priority", "Status"];

const AssetProfile = () => {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { clientId } = useSelector(getUserData);
  const priorityLevels = useSelector(getPriorityLevels);

  const [asset, setAsset] = useState<Asset | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("general");
  const [associatedWorkOrders, setAssociatedWorkOrders] = useState<AssociatedWorkOrder[]>([]);
  const [isAssociatedWorkOrdersLoading, setIsAssociatedWorkOrdersLoading] = useState(false);

  const locationState = location.state as AssetProfileLocationState | undefined;

  useEffect(() => {
    dispatch(setPageNameHeader("Asset Profile"));
    return () => {
      dispatch(setPageNameHeader(""));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!assetId) {
      setAsset(null);
      return;
    }
    if (locationState?.asset && String(locationState.asset.id) === assetId) {
      setAsset(locationState.asset);
      return;
    }
    const fetchAsset = async () => {
      setIsLoading(true);
      try {
        const found = await fetchAssetById(assetId);
        setAsset(found ?? null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAsset();
  }, [assetId, locationState?.asset]);

  const onClickBreadcrumb = (route: ID | null) => {
    if (typeof route === "string" && route === "/assets") {
      dispatch(setOpenedAssetsPage(1));
    }
    typeof route === "string" && navigate(route);
  };

  const onWorkOrderClick = (wonum: string) => {
    if (!wonum || wonum === "N/A") {
      return;
    }
    navigate(`/workrequests/${wonum}`, { state: { ticketNumber: wonum } });
  };

  const getFirstNonEmpty = (...values: Array<string | number | undefined | null>) => {
    const value = values.find((item) => item !== undefined && item !== null && String(item).trim() !== "");
    return value ? String(value) : "N/A";
  };

  const formatInstallYear = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return dateString;
      }
      return date.getFullYear().toString();
    } catch {
      return dateString;
    }
  };

  const fetchAssetById = async (assetId: string): Promise<Asset | undefined> => {
    let byIdAssets: Asset[] = [];
    try {
      const byIdResponse = await FirebaseDataService.getAssets({ id: assetId }, 1, 1);
      byIdAssets = byIdResponse?.data?.assets ?? [];
    } catch {
      byIdAssets = [];
    }
    const directMatch = byIdAssets.find((item) => String(item.id) === assetId);
    if (directMatch) return directMatch;

    const firstPageResponse = await FirebaseDataService.getAssets(undefined, DEFAULT_PAGE_SIZE, 1);
    const firstPageAssets = firstPageResponse?.data?.assets ?? [];
    const firstPageTotal = firstPageResponse?.data?.total ?? 0;
    const firstPageMatch = firstPageAssets.find(
      (item) => String(item.id) === assetId || item.assetnum === assetId || item.assetCode === assetId,
    );
    if (firstPageMatch) return firstPageMatch;
    if (firstPageTotal <= firstPageAssets.length) return undefined;

    const allAssetsResponse = await FirebaseDataService.getAssets(undefined, firstPageTotal, 1);
    const allAssets = allAssetsResponse?.data?.assets ?? [];
    return allAssets.find(
      (item) => String(item.id) === assetId || item.assetnum === assetId || item.assetCode === assetId,
    );
  };

  const mappedAssetData = useMemo(() => {
    const dynamicAsset = (asset || {}) as Asset & Record<string, any>;

    const locationName = getFirstNonEmpty(
      dynamicAsset.location_description,
      dynamicAsset["LOCATIONS.DESCRIPTION"],
      dynamicAsset.locations_description,
      dynamicAsset.stationName,
    );
    const assetName = getFirstNonEmpty(dynamicAsset.description, dynamicAsset["ASSET.DESCRIPTION"], DEMO_ASSET.title);

    return {
      title: assetName,
      status: getFirstNonEmpty(dynamicAsset.status_description, dynamicAsset.status, DEMO_ASSET.status),

      stationName: locationName,
      stationAddress: getFirstNonEmpty(
        dynamicAsset.salocationsadesc,
        dynamicAsset.SALOCATIONSADESC,
        dynamicAsset["ASSET.SALOCATIONSADESC"],
      ),
      assetId: getFirstNonEmpty(dynamicAsset.assetnum, dynamicAsset["ASSET.ASSETNUM"], dynamicAsset.id),
      category: getFirstNonEmpty(dynamicAsset.assettype, dynamicAsset["ASSET.ASSETTYPE"], dynamicAsset.category),

      roomLocation: getFirstNonEmpty(
        dynamicAsset.assetlocation,
        dynamicAsset.ASSETLOCATION,
        dynamicAsset["ASSET.ASSETLOCATION"],
        dynamicAsset.location,
      ),
      manufacturer: getFirstNonEmpty(
        dynamicAsset["COMPANIES.NAME"],
        dynamicAsset.companiesName,
        dynamicAsset.companyName,
        dynamicAsset.manufacturer,
        dynamicAsset.vendor,
      ),
      model: getFirstNonEmpty(dynamicAsset.assetmodel, dynamicAsset.ASSETMODEL, dynamicAsset["ASSET.ASSETMODEL"]),
      serialNumber: getFirstNonEmpty(dynamicAsset.serialnum, dynamicAsset["ASSET.SERIALNUM"]),
      yearInstalled: getFirstNonEmpty(
        formatInstallYear(dynamicAsset.installdate),
        formatInstallYear(dynamicAsset.INSTALLDATE),
        dynamicAsset["ASSET.INSTALLDATE"],
      ),
    };
  }, [asset]);

  // Use dynamic data for requested fields and keep demo fallbacks for the rest of the page.
  const data = {
    ...DEMO_ASSET,
    ...mappedAssetData,
    id: mappedAssetData.assetId,
  };

  useEffect(() => {
    if (activeTab !== "workRequests") {
      return;
    }

    const loadAssociatedWorkOrders = async () => {
      const dynamicAsset = (asset || {}) as Asset & Record<string, any>;
      const assetNumber = getFirstNonEmpty(dynamicAsset.assetnum, dynamicAsset["ASSET.ASSETNUM"], assetId);

      if (assetNumber === "N/A") {
        setAssociatedWorkOrders([]);
        return;
      }

      setIsAssociatedWorkOrdersLoading(true);
      try {
        const response = await FirebaseDataService.getWorkOrdersByAssetNum(assetNumber);
        const workOrdersData = Array.isArray(response?.data?.workOrders) ? response.data.workOrders : [];

        const mappedOrders = workOrdersData.map((item, index: number) => {
          const workOrder = item as Record<string, any>;
          const wonum = getFirstNonEmpty(item.wonum, workOrder.wonum, workOrder.displaywonum);

          return {
            id: getFirstNonEmpty(item.wonum, item.workorderid, `${index + 1}`),
            wonum: wonum !== "N/A" ? wonum : undefined,
            description: getFirstNonEmpty(workOrder.description, workOrder.name),
            priority: getFirstNonEmpty(workOrder.priorityDescription, workOrder.priority, workOrder.priorityId),
            status: getFirstNonEmpty(workOrder.statusDescription, workOrder.status, workOrder.status_description),
            priorityId: Number(workOrder.priorityId),
            clientId: workOrder.clientId,
          };
        });

        setAssociatedWorkOrders(mappedOrders);
      } catch {
        setAssociatedWorkOrders([]);
      } finally {
        setIsAssociatedWorkOrdersLoading(false);
      }
    };

    loadAssociatedWorkOrders();
  }, [activeTab, asset, assetId]);

  const ASSET_DETAILS_BREADCRUMBS = [
    { id: "/", name: "Home" },
    { id: "/assets", name: "Assets" },
    { id: "/assets/:id", name: ":name" },
  ];

  return (
    <Container>
      <LoadingContainer isLoading={isLoading}>
        {/* Breadcrumb */}
        <TopBar>
          <BreadCrumbs
            folderStack={generateBreadcrumbs(ASSET_DETAILS_BREADCRUMBS, {
              id: assetId || "unknown",
              name: data.title || "Asset Details",
            })}
            onClickItem={onClickBreadcrumb}
          />
        </TopBar>

        <Content>
          <ContentFrame>
            <FormContainer>
              <HorizontalTabs
                tabs={[
                  {
                    eventKey: "general",
                    label: "General Information",
                    content: <GeneralInformation data={data} />,
                  },
                  {
                    eventKey: "workRequests",
                    label: "Associated Work Requests",
                    content: (
                      <AssetWorkOrders
                        headers={ASSOCIATED_WORK_ORDER_HEADERS}
                        isLoading={isAssociatedWorkOrdersLoading}
                        workOrders={associatedWorkOrders}
                        priorityLevels={priorityLevels}
                        clientId={clientId}
                        onWorkOrderClick={onWorkOrderClick}
                      />
                    ),
                  },
                ]}
                activeTab={activeTab}
                setActiveTab={(value) => setActiveTab(value as Tab)}
              />
            </FormContainer>
          </ContentFrame>
        </Content>
      </LoadingContainer>
    </Container>
  );
};

const FormContainer = styled.div`
  padding: 0;
  background: white;
`;

export const ErrorMessage = styled.div`
  color: ${colors.red};
  padding: 10px;
`;

export default AssetProfile;
